import { useState } from "react";
import style from "./create-monday-item-sections.module.css";
import { useBoardTasks } from "frontend/hooks/use-board-integrations";
import { useBoardRequiredColumns } from "frontend/hooks/use-monday-boards-picker";
import { useAtomValue } from "jotai";
import { submenuDataAtom, syncServiceAtom, undoRedoStackAtom, utilsAtom } from "state-atoms";
import { IntegrationType } from "shared/integrations/integration";
import { CanvasElement, TextEnabledElement } from "shared/datamodel/schemas";
import { zip } from "rambda";
import {
  IdKeyPair,
  useFixTemplatesWithMondayItems,
} from "frontend/canvas-designer-new/integrations/monday-item-canvas-templates";
import { CreateMondayItemMappingsProps } from "../create-monday-item-menu";
import { StatusColumnPicker } from "../pickers/status-column-picker";
import { ExpandableContent, PrimaryButton } from "../components";
import consts from "shared/consts";
import { useFeatureFlag } from "frontend/hooks/use-feature-flag/use-feature-flag";
import { useDynamicElementActions } from "frontend/canvas-designer-new/element-dynamic-data/hooks/use-dynamic-element-actions";
import { DynamicElement } from "frontend/canvas-designer-new/element-dynamic-data/dynamic-data-utils";
import tracking from "frontend/tracking";
import { MondayBoard, MondayColumn } from "shared/integrations/mapper";
import { GeneralColumnEditor } from "../pickers/general-column-editor";
import InfoIcon from "frontend/icons/info";

export function SimpleElementsConversion({
  documentId,
  nodes,
  selectedBoard,
  selectedGroupId,
  selectedItemId,
  isMultipleElements,
  isSubitem = false,
  onCreatedItems,
  integrationId,
}: CreateMondayItemMappingsProps) {
  const { createItems, createSubitems } = useBoardTasks(documentId);

  const [isCreatingItem, setIsCreatingItem] = useState(false);
  const [selectedColumnValues, setSelectedColumnValues] = useState<{
    [columnId: string]: { index: string };
  }>({});
  const [error, setError] = useState<string | null>(null);

  const canvasUtils = useAtomValue(utilsAtom(documentId));
  const syncService = useAtomValue(syncServiceAtom);
  const submenuData = useAtomValue(submenuDataAtom);
  const undoRedoStack = useAtomValue(undoRedoStackAtom);
  const fixElementsAfterCreation = useFixTemplatesWithMondayItems();
  const canCreateItem = !!selectedBoard && (isSubitem ? !!selectedItemId : !!selectedGroupId);

  const isDataLayerEnabled = useFeatureFlag("element-data-layer");
  const allowConvertToDataLayer = [...nodes].every((node) =>
    consts.ELEMENTS_SUPPORTING_DATA_LAYER.includes(node.attrs.type)
  );
  const [convertToCard, setConvertToCard] = useState(false);

  const { addIntegrationToElement, addFieldToElement } = useDynamicElementActions();
  const { data: requiredColumns, isLoading: isLoadingColumns } = useBoardRequiredColumns(
    integrationId ?? "",
    selectedBoard?.id ?? ""
  );

  async function createItemClicked() {
    if (!selectedBoard || !nodes) return;
    canvasUtils?.setEditingElementId(null);
    canvasUtils?.setSelectedElementIds([]);

    const replacedElements: { id: string; element: TextEnabledElement }[] = [];
    for (const node of nodes) {
      const element = (node.attrs.element ?? {}) as TextEnabledElement & { fill?: string };
      const newElement = { ...element };
      if (typeof newElement.fill === "string") {
        newElement.fill = undefined;
      }
      if (typeof newElement.text === "string" && newElement.text.length > 0) {
        replacedElements.push({ id: node.attrs.id, element: newElement });
      }
    }
    setIsCreatingItem(true);
    try {
      const titles = replacedElements.reduce((acc, element) => {
        acc[element.id] = element.element.text;
        return acc;
      }, {} as { [elementId: string]: string });
      let promise: ReturnType<typeof createItems>;
      if (isSubitem) {
        if (!selectedItemId || !selectedBoard.subitem_board) {
          return;
        }
        promise = createSubitems({
          boardId: selectedBoard.id,
          subitemsBoardId: selectedBoard.subitem_board.id,
          parentItemId: selectedItemId,
          columnValues: selectedColumnValues,
          columns: selectedBoard.subitem_board.columns,
          titles,
        });
      } else {
        promise = createItems(selectedBoard.id, titles, selectedGroupId, selectedColumnValues, selectedBoard.columns);
      }
      const { integrationId, itemIds, error: createItemError } = await promise;
      tracking.trackAnalyticsEvent("convert-to-monday-item-clicked", {
        isSubitem,
        isMultipleElements,
        convertToDataLayer: !convertToCard, // originally this parameter was "keep original shape", so I keep the meaning of it
      });

      if (createItemError) {
        setError(createItemError.toString());
        tracking.trackAnalyticsEvent("convert-to-monday-item-failed", {
          error: createItemError.toString(),
          category: consts.TRACKING_CATEGORY.INTEGRATIONS,
        });
        return;
      }

      if (integrationId && itemIds && !convertToCard) {
        const sequenceId = `create-monday-multiple-items`;
        undoRedoStack?.startSequence(sequenceId);
        const selectedElementIds = [];
        for (const node of nodes) {
          const elementId = node.attrs.id;
          const element = node.attrs.element as DynamicElement;
          const itemId = itemIds[elementId];

          // eslint-disable-next-line no-await-in-loop
          await addIntegrationToElement(elementId, integrationId, itemId, {
            clearText: true,
            sequenceId,
          });

          const elementFontProps = {
            fontSize: Number(element.fontSize),
            textColor: element.textColor,
            font: element.font,
            align: element.align,
          };
          const textProperties = {
            ...submenuData?.textBlock,
            ...elementFontProps,
          };

          // eslint-disable-next-line no-await-in-loop
          const addedElementId = await addFieldToElement(elementId, element, integrationId, itemId, "name", {
            fieldValue: element.text,
            textProperties,
            sequenceId,
          });
          selectedElementIds.push(elementId);
          if (addedElementId) {
            selectedElementIds.push(addedElementId);
          }
        }
        setIsCreatingItem(false);
        canvasUtils?.setSelectedElementIds(selectedElementIds);
        undoRedoStack?.endSequence(sequenceId);

        onCreatedItems();
        return;
      }

      if (integrationId && itemIds && convertToCard) {
        const newElementsToReplace = replacedElements.reduce((acc, element) => {
          acc[element.id] = { itemId: itemIds[element.id], element: element.element, elementId: element.id };
          return acc;
        }, {} as { [elementId: string]: { itemId: string; element: CanvasElement; elementId: string } });
        const combinedIds = await replaceSelectedElementsWithMondayItems(newElementsToReplace, integrationId);
        fixElementsAfterCreation("timeline", combinedIds);
      }
    } catch (error) {
      console.error("failed to create item", error);
    } finally {
      setIsCreatingItem(false);
    }
  }

  async function replaceSelectedElementsWithMondayItems(
    elements: { [id: string]: { itemId: string; element: CanvasElement; elementId: string } },
    integrationId: string
  ): Promise<IdKeyPair> {
    if (!canvasUtils) {
      return [];
    }
    const itemsToCreate = Object.values(elements);
    const { newIds, oldIds } = await canvasUtils.addIntegrationItems(
      itemsToCreate,
      integrationId,
      IntegrationType.monday,
      syncService,
      false
    );
    canvasUtils.replaceCanvasElements(newIds, oldIds);
    onCreatedItems();
    return zip(newIds, oldIds);
  }

  let columns: MondayBoard["columns"] = [];
  if (selectedBoard) {
    columns = isSubitem && selectedBoard.subitem_board ? selectedBoard.subitem_board.columns : selectedBoard.columns;
  }

  const columnsWithRequiredFlag = columns.map((col: MondayColumn) => ({
    ...col,
    required: selectedBoard
      ? requiredColumns?.[selectedBoard?.id]?.required_column_ids?.includes(col.id) ?? false
      : false,
  }));
  const columnsToRender = columnsWithRequiredFlag.filter((column) => column.required || column.type == "status");

  const hasUnfilledRequiredColumns = columnsWithRequiredFlag.some((column) => {
    if (!column.required) return false;
    if (column.type === "status") {
      return !selectedColumnValues[column.id]?.index;
    }
    return true;
  });

  const renderLoadingColumns = () => {
    return (
      <div>
        <div className={style.loadingColumn}>Loading...</div>
      </div>
    );
  };

  return (
    <>
      <ExpandableContent open={!!selectedBoard}>
        {isLoadingColumns ? (
          renderLoadingColumns()
        ) : (
          <>
            <div className={style.columnsAreaTitle}>Columns</div>
            {columnsToRender.map((column) => (
              <div className={style.columnContainer} key={column.id}>
                <div title={column.title} className={style.columnTitle}>
                  {column.title}
                </div>
                {column.type == "status" ? (
                  <StatusColumnPicker
                    column={column}
                    selected={selectedColumnValues[column.id]?.index}
                    setSelected={(index) =>
                      setSelectedColumnValues((previous) => ({ ...previous, [column.id]: { index: index } }))
                    }
                  />
                ) : (
                  <>
                    <GeneralColumnEditor column={column} />
                    <span className={style.notSupportedColumn}>
                      <InfoIcon /> Only added through board
                    </span>
                  </>
                )}
              </div>
            ))}
          </>
        )}
      </ExpandableContent>

      {isDataLayerEnabled && allowConvertToDataLayer && (
        <label className={style.checkbox}>
          <input
            type="checkbox"
            checked={convertToCard}
            onChange={(ev) => {
              if (ev.target.checked) {
                tracking.trackAnalyticsEvent("convert-to-monday-item-keep-original-shape-enabled");
              }
              setConvertToCard(ev.target.checked);
            }}
          />
          <span />
          Display task as a card
        </label>
      )}

      {error && (
        <div className={style.error}>
          <InfoIcon />
          Unable to convert item
        </div>
      )}
      <PrimaryButton
        onClick={createItemClicked}
        disabled={isCreatingItem || !canCreateItem || hasUnfilledRequiredColumns || Boolean(error)}
      >
        {isCreatingItem
          ? isMultipleElements
            ? "Creating items..."
            : "Creating item..."
          : `Convert to monday ${isSubitem ? "sub" : ""}item${isMultipleElements ? "s" : ""}`}
      </PrimaryButton>
    </>
  );
}
